import React from 'react';
import { Row, Col, Card, Button, Statistic, InputNumber, Form } from 'antd';
import {
  parseFieldAmount,
  formatAmount,
  formatFieldAmount,
  formatCurrencyRounded,
} from 'vdlib';
import { I18n as I } from 'react-redux-i18n';

const BidComponent = ({
  auction,
  noBidsDisplay,
  onSubmit,
  userId,
  merchantFees,
  membershipID,
}) => {
  // extract an object containing MY last bid and BEST bid
  const bids = React.useMemo(
    () =>
      userId &&
      auction &&
      Array.isArray(auction.Bids) &&
      auction.Bids.length > 0
        ? {
            best: auction.Bids.reduce(
              (best, current) =>
                current.Amount > best.Amount ? current : best,
              { Amount: 0 }
            ),
            my: auction.Bids.find(b => b.BidderID === userId),
          }
        : {
            best: { Amount: 0 },
            my: { Amount: 0 },
          },
    [auction, userId]
  );

  const [form] = Form.useForm();

  const {
    my: { Amount: current },
  } = bids;
  const { BidStep: bidStep } = auction;

  const [bidAmount, _setBidAmount] = React.useState(current + bidStep || 0);
  const [feeAmount, setFeeAmount] = React.useState(current + bidStep || 0);

  // Rounding bid amount setter
  const setBidAmount = React.useCallback(
    amount => {
      const newAmount = Math.round(amount / bidStep) * bidStep;
      _setBidAmount(newAmount);
    },
    [_setBidAmount, bidStep]
  );

  React.useMemo(() => {
    var fee = merchantFees
      ? merchantFees.find(
          x =>
            bidAmount >= x.AmountFrom &&
            bidAmount < x.AmountTo &&
            x.MembershipID === membershipID
        )
      : 0;

    let feeAmount = 0;

    if (fee) {
      if (fee.FeePercentage) {
        feeAmount = (bidAmount * fee.FeePercentage) / 100;
      } else if (fee.FeeAmount) {
        feeAmount = fee.FeeAmount;
      }
    }

    setFeeAmount(feeAmount);
  }, [bidAmount, merchantFees]); //eslint-disable-line

  React.useEffect(() => {
    if (bids.my) {
      const newAmount = bids.my.Amount + (bids.my < bids.best ? bidStep : 0);
      _setBidAmount(newAmount);
      form.setFieldsValue({ bidAmount: newAmount });
    }
  }, [bids]); //eslint-disable-line

  const onLinkAdd = React.useCallback(
    linkAmount => {
      const newBidAmount = Math.max(bidAmount, bids.best.Amount) + linkAmount;
      form.setFieldsValue({ bidAmount: newBidAmount });
      setBidAmount(newBidAmount);
    },
    [bids, bidAmount, form, setBidAmount]
  );

  return auction ? (
    <div style={{ width: '100%' }}>
      <Row gutter={[8, 8]}>
        <Col span={noBidsDisplay ? 24 : 16}>
          <Card
            style={{ backgroundColor: '#f0f0f0' }}
            bodyStyle={{ padding: 10 }}
          >
            <Form
              form={form}
              name="bid-component"
              initialValues={{ bidAmount: bidAmount }}
              onFinish={() => onSubmit(bidAmount, auction)}
            >
              <Row>
                <Col span={16}>
                  <Row>
                    <div style={{ marginLeft: 40 }}>
                      <Button
                        type="link"
                        onClick={() => onLinkAdd(bidStep)}
                        style={{ padding: 5 }}
                      >
                        {`+${bidStep}`}
                      </Button>
                      <Button
                        type="link"
                        onClick={() => onLinkAdd(bidStep * 2)}
                        style={{ padding: 5 }}
                      >
                        {`+${bidStep * 2}`}
                      </Button>
                      <Button
                        type="link"
                        onClick={() => onLinkAdd(bidStep * 4)}
                        style={{ padding: 5 }}
                      >
                        {`+${bidStep * 4}`}
                      </Button>
                    </div>
                  </Row>
                  <Row justify="start">
                    <Form.Item
                      name="bidAmount"
                      label="Bid"
                      rules={[
                        { required: true, message: 'Needed' },
                        () => ({
                          validator(rule, value) {
                            if (
                              value &&
                              auction.OnlineMode &&
                              value <= bids.best.Amount
                            ) {
                              return Promise.reject(
                                `Επιτρεπτή προσφορά μόνο μεγαλύτερη από την υψηλότερη`
                              );
                            } else if (!value || value % bidStep !== 0) {
                              return Promise.reject(
                                `${formatAmount(
                                  (Math.floor(value / bidStep) + 1) * bidStep
                                )} ή ${formatAmount(
                                  (Math.floor(value / bidStep) + 1) * bidStep -
                                    bidStep
                                )}`
                              );
                            } else if (
                              !auction.OnlineMode &&
                              current > 0 &&
                              value > current + bidStep * 8
                            ) {
                              return Promise.reject(
                                `Επιτρεπτό ποσό επόμενης προσφοράς από ${formatFieldAmount(
                                  current + bidStep
                                )} έως ${formatFieldAmount(
                                  current + bidStep * 8
                                )}`
                              );
                            }
                            // } else if (
                            //   auction.OnlineMode &&
                            //   bids.best.Amount > 0 &&
                            //   value > bids.best.Amount + bidStep * 8
                            // ) {
                            //   return Promise.reject(
                            //     `Επιτρεπτό ποσό επόμενης προσφοράς από ${formatFieldAmount(
                            //       bids.best.Amount + bidStep
                            //     )} έως ${formatFieldAmount(
                            //       bids.best.Amount + bidStep * 8
                            //     )}`
                            //   );
                            // }
                            else {
                              return Promise.resolve();
                            }
                          },
                        }),
                      ]}
                    >
                      <InputNumber
                        min={auction.OnlineMode ? bids.best.Amount : 0}
                        size="middle"
                        step={bidStep}
                        onChange={setBidAmount}
                        parser={value => parseFieldAmount(value)}
                        formatter={value => formatFieldAmount(value)}
                        style={{ width: 120 }}
                      />
                    </Form.Item>
                  </Row>
                  <Row justify="start">
                    <span
                      style={{
                        fontStyle: 'italic',
                        fontSize: 12,
                        marginRight: 3,
                      }}
                    >
                      {`${I.t('FEE')}:`}
                    </span>
                    <span
                      style={{
                        fontStyle: 'italic',
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {formatCurrencyRounded(feeAmount)}
                    </span>
                  </Row>
                </Col>
                <Col span={8}>
                  <Form.Item>
                    <Button
                      size="large"
                      type="danger"
                      htmlType="submit"
                      style={{ width: '100%', height: 75, marginTop: 7 }}
                    >
                      BID
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
        {noBidsDisplay ? null : (
          <Col span={8}>
            <Row style={{ height: 110 }}>
              <Card
                bordered={true}
                style={{
                  backgroundColor: '#f0f0f0',
                  width: '100%',
                }}
                bodyStyle={{ padding: 10 }}
              >
                <Statistic
                  title="Τρέχουσα Προσφορά:"
                  value={current ? formatFieldAmount(current) : 0}
                />
              </Card>
            </Row>
          </Col>
        )}
      </Row>
    </div>
  ) : null;
};

export default BidComponent;
